import { type StoreName } from '@bluebrick/discounts-service-module'
import { type DateTime, type Duration } from 'luxon'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { isDateTimeAfter, isDateTimeBefore, now } from '@/common/dateUtils'
import { usePreferencesStore } from '@/features/preferences/usePreferencesStore'
import { useStoresStore } from '@/features/stores/useStoresStore'
import { useTelemetry } from '@/features/telemetry/useTelemetry'

const DATA_RANGE_IN_MONTHS = 36

export const HALF_DATA_RANGE_IN_MONTHS = DATA_RANGE_IN_MONTHS / 2

const VIEW_RANGE_IN_MONTHS = 12
const HALF_VIEW_RANGE_IN_MONTHS = VIEW_RANGE_IN_MONTHS / 2

const today = now().toUTC().startOf('day')

const useCalendarInnerStore = defineStore('calendarInner', () => {
	const selectedDatePerStore = ref<Map<StoreName, DateTime<true>>>(new Map())
	/**
	 * Will just use a fixed range of data (1.5 year back and 1.5 year forward) without
	 * possibility of changing it.
	 * Within that data range we will have a 12-month visible range which can be moved back and forth
	 * withing the data range:
	 *
	 * +-------------------------------- DATA RANGE (3 years) --------------------------------+
	 * |                 +--------------------------------+                                   |
	 * |                 |       VIEW RANGE (1 year)      |                                   |
	 * |                 +--------------------------------+                                   |
	 * +--------------------------------------------------------------------------------------+
	 */
	const dataRangeStartDate = ref(today.minus({ months: HALF_DATA_RANGE_IN_MONTHS }).startOf('month'))
	const dataRangeEndDate = ref(today.plus({ months: HALF_DATA_RANGE_IN_MONTHS }).endOf('month'))
	const visibleRangePerStoreStartDate = ref<Map<StoreName, DateTime<true>>>(new Map())
	const visibleRangePerStoreEndDate = ref<Map<StoreName, DateTime<true>>>(new Map())

	return {
		selectedDatePerStore,
		dataRangeStartDate,
		dataRangeEndDate,
		visibleRangePerStoreStartDate,
		visibleRangePerStoreEndDate
	}
})

export const useCalendarStore = defineStore('calendar', () => {
	const { trackEvent } = useTelemetry()

	const innerStore = useCalendarInnerStore()

	const preferencesStore = usePreferencesStore()
	const storesStore = useStoresStore()

	const dataRangeStartDate = computed(() => innerStore.dataRangeStartDate)
	const dataRangeEndDate = computed(() => innerStore.dataRangeEndDate)

	const selectedDatePerStore = computed(() => innerStore.selectedDatePerStore)

	const currentDateRangeHash = computed(() => `${dataRangeStartDate.value.toISO()}-${dataRangeEndDate.value.toISO()}`)

	function getSelectedDate(store: StoreName): DateTime<true> {
		const result = innerStore.selectedDatePerStore.get(store) ?? today

		if (!innerStore.selectedDatePerStore.has(store)) {
			innerStore.selectedDatePerStore.set(store, result)
		}

		return result
	}

	function calcVisibleRangeStartDate(referenceDate: DateTime = today): DateTime<true> {
		return referenceDate.minus({ months: HALF_VIEW_RANGE_IN_MONTHS }).startOf('month')
	}

	function calcVisibleRangeEndDate(referenceDate: DateTime = today): DateTime<true> {
		return referenceDate.plus({ months: HALF_VIEW_RANGE_IN_MONTHS }).startOf('month').minus({ day: 1 })
	}

	function getVisibleRangeStartDate(store: StoreName): DateTime<true> {
		const result = innerStore.visibleRangePerStoreStartDate.get(store) ?? calcVisibleRangeStartDate()

		if (!innerStore.visibleRangePerStoreStartDate.has(store)) {
			innerStore.visibleRangePerStoreStartDate.set(store, result)
		}

		return result
	}

	function getVisibleRangeEndDate(store: StoreName): DateTime<true> {
		const result = innerStore.visibleRangePerStoreEndDate.get(store) ?? calcVisibleRangeEndDate()

		if (!innerStore.visibleRangePerStoreEndDate.has(store)) {
			innerStore.visibleRangePerStoreEndDate.set(store, result)
		}

		return result
	}

	function canMoveBack(store: StoreName) {
		return isDateTimeAfter(getVisibleRangeStartDate(store).startOf('day'), dataRangeStartDate.value)
	}

	function canMoveForward(store: StoreName) {
		return isDateTimeBefore(getVisibleRangeEndDate(store).endOf('day'), dataRangeEndDate.value)
	}

	function goToPreviousPeriod(store: StoreName) {
		if (!canMoveBack(store)) {
			return
		}

		let endDate = getVisibleRangeStartDate(store).minus({ month: 1 }).endOf('month')
		let startDate = endDate.minus({ months: VIEW_RANGE_IN_MONTHS - 1 }).startOf('month')

		if (isDateTimeBefore(startDate, dataRangeStartDate.value)) {
			startDate = dataRangeStartDate.value.startOf('month')
			endDate = startDate.plus({ months: VIEW_RANGE_IN_MONTHS - 1 }).endOf('month')
		}

		innerStore.visibleRangePerStoreStartDate.set(store, startDate)
		innerStore.visibleRangePerStoreEndDate.set(store, endDate)
		innerStore.selectedDatePerStore.set(store, endDate)

		trackEvent('user', `Go to the previous period - ${preferencesStore.timelineResolutionPreset}`, {
			selectedDate: getSelectedDate(store).toISO(),
			startDate: startDate.toISO(),
			endDate: endDate.toISO()
		})
	}

	function goToNextPeriod(store: StoreName) {
		if (!canMoveForward(store)) {
			return
		}

		let startDate = getVisibleRangeEndDate(store).plus({ month: 1 }).startOf('month')
		let endDate = startDate.plus({ months: VIEW_RANGE_IN_MONTHS - 1 }).endOf('month')

		if (isDateTimeAfter(endDate, dataRangeEndDate.value)) {
			endDate = dataRangeEndDate.value.endOf('month')
			startDate = endDate.minus({ months: VIEW_RANGE_IN_MONTHS - 1 }).startOf('month')
		}

		innerStore.visibleRangePerStoreStartDate.set(store, startDate)
		innerStore.visibleRangePerStoreEndDate.set(store, endDate)
		innerStore.selectedDatePerStore.set(store, startDate)

		trackEvent('user', `Go to the next period - ${preferencesStore.timelineResolutionPreset}`, {
			selectedDate: getSelectedDate(store).toISO(),
			startDate: startDate.toISO(),
			endDate: endDate.toISO()
		})
	}

	function setDefaultDatesRange() {
		storesStore.stores.forEach((store) => {
			updateDatesRange(store.id, today)
		})

		preferencesStore.setDefaultTimelineResolution(VIEW_RANGE_IN_MONTHS)
	}

	function setSelectedDate(store: StoreName, date: DateTime<true>, includeRangeUpdate?: boolean) {
		if (isDateTimeBefore(date, dataRangeStartDate.value)) {
			// Given date is before the start of available data range - use data range start date instead
			innerStore.selectedDatePerStore.set(store, dataRangeStartDate.value)
		} else if (isDateTimeAfter(date, dataRangeEndDate.value)) {
			// Given date is after the end of available data range - use data range end date instead
			innerStore.selectedDatePerStore.set(store, dataRangeEndDate.value)
		} else {
			// Given date fits withing the available data range so we just use it
			innerStore.selectedDatePerStore.set(store, date.toUTC().startOf('day'))
		}

		if (includeRangeUpdate) {
			updateDatesRange(store)
		}
	}

	/**
	 * Updates dates of visible data "window" based on given reference date (`[*]`). The default behaviour
	 * is to subtract `HALF_VIEW_RANGE_IN_MONTHS` from ref date to get start date or the window and add
	 * `HALF_VIEW_RANGE_IN_MONTHS` to ref date to get end date of the window:
	 * +-------------------------[*]------- DATA RANGE (3 years) --------------------------------+
	 * |         +----------------|----------------+                                             |
	 * |         | 1/2 VIEW RANGE | 1/2 VIEW RANGE |                                             |
	 * |         +----------------|----------------+                                             |
	 * +--------------------------|--------------------------------------------------------------+
	 * If reference date is less than `HALF_VIEW_RANGE_IN_MONTHS` after the beginning of DATA RANGE
	 * then we adjust visible "window" to still fit withing DATA RANGE. For example:
	 * +---------[*]-------------------- DATA RANGE (3 years) -----------------------------------+
	 * +----------|----------------------+                                                       |
	 * | 1/4 VIEW |    3/4 VIEW RANGE    |                                                       |
	 * | RANGE    |                      |                                                       |
	 * +----------|----------------------+                                                       |
	 * +----------|------------------------------------------------------------------------------+
	 * If reference date is less `HALF_VIEW_RANGE_IN_MONTHS` before the end of DATA RANGE then we
	 * also adjust visible "window" to still fit withing DATA RANGE. For example:
	 * +-------------------------------- DATA RANGE (3 years) -----------------------[*]---------+
	 * |                                                       +----------------------|----------+
	 * |                                                       |       3/4 VIEW       | 1/4 VIEW |
	 * |                                                       |                      | RANGE    |
	 * |                                                       +----------------------|----------+
	 * +------------------------------------------------------------------------------|----------+
	 * But the size of visible data "window" is always the same.
	 */
	function updateDatesRange(store: StoreName, referenceDate = getSelectedDate(store)) {
		let startDate = calcVisibleRangeStartDate(referenceDate)
		let endDate = calcVisibleRangeEndDate(referenceDate)
		let diff: Duration | undefined = undefined

		if (isDateTimeBefore(startDate, dataRangeStartDate.value)) {
			// reference date is less than `HALF_VIEW_RANGE_IN_MONTHS` after the beginning of DATA RANGE
			diff = startDate.diff(dataRangeStartDate.value, 'months')

			if (diff.isValid && diff.months !== 0) {
				startDate = startDate.plus({ months: Math.abs(diff.months) }).startOf('month')
				endDate = startDate.plus({ months: VIEW_RANGE_IN_MONTHS - 1 }).endOf('month')
			}
		} else if (isDateTimeAfter(endDate, dataRangeEndDate.value)) {
			// reference date is less `HALF_VIEW_RANGE_IN_MONTHS` before the end of DATA RANGE
			diff = endDate.diff(dataRangeEndDate.value, 'months')

			if (diff.isValid && diff.months !== 0) {
				endDate = endDate.minus({ months: Math.abs(diff.months) }).endOf('month')
				startDate = endDate.minus({ months: VIEW_RANGE_IN_MONTHS - 1 }).startOf('month')
			}
		}

		innerStore.visibleRangePerStoreStartDate.set(store, startDate)
		innerStore.visibleRangePerStoreEndDate.set(store, endDate)

		trackEvent('app', 'Update visible data range', {
			selectedDate: referenceDate.toISO(),
			startDate: startDate.toISO(),
			endDate: endDate.toISO(),
			dataRangeStartDate: dataRangeStartDate.value.toISO(),
			dataRangeEndDate: dataRangeEndDate.value.toISO(),
			store,
			timelineResolution: preferencesStore.timelineResolutionPreset
		})
	}

	return {
		dataRangeStartDate,
		dataRangeEndDate,
		currentDateRangeHash,
		selectedDatePerStore,
		getSelectedDate,
		getVisibleRangeStartDate,
		getVisibleRangeEndDate,
		canMoveBack,
		canMoveForward,
		goToPreviousPeriod,
		goToNextPeriod,
		setDefaultDatesRange,
		setSelectedDate
	}
})
